import BpkBreakpoint, {
  BREAKPOINTS,
} from '@skyscanner-internal/backpack-web/bpk-component-breakpoint';
import BpkImage, {
  withLazyLoading,
  withLoadingBehavior,
} from '@skyscanner-internal/backpack-web/bpk-component-image';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classnames from 'classnames';

import ContentBlock from '../ContentBlock';
import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type { ContentBlockWithImageProps } from '@skyscanner-internal/landing-pages-shared-types/types-shared/ContentBlockWithImageProps';

import STYLES from './ContentBlockWithImage.module.scss';

const documentIfExists = globalThis.window ? document : null;
const LazyLoadedImage = withLoadingBehavior(
  withLazyLoading(BpkImage, documentIfExists),
);

const DESKTOP_ASPECT_RATIO = 496 / 660;
const MOBILE_ASPECT_RATIO = 16 / 9;

const ContentBlockWithImage = (props: ContentBlockWithImageProps) => {
  const { image, imagePosition, isMobile, sections } = props;
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!sections?.length) {
    return null;
  }

  return (
    <div
      data-testid="content-block-with-image"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
      className={STYLES.ContentBlockWithImage}
    >
      <div
        className={classnames(
          STYLES.ContentBlockWithImage__layout,
          imagePosition === 'right' &&
            STYLES.ContentBlockWithImage__layoutReversed,
        )}
      >
        {image && (
          <div className={STYLES.ContentBlockWithImage__imageContainer}>
            <BpkBreakpoint query={BREAKPOINTS.MOBILE} matchSSR={isMobile}>
              {(isMobileViewport: boolean) => (
                <LazyLoadedImage
                  altText={image.altText || ''}
                  src={image.imageUrl}
                  aspectRatio={
                    isMobileViewport
                      ? MOBILE_ASPECT_RATIO
                      : DESKTOP_ASPECT_RATIO
                  }
                  className={STYLES.ContentBlockWithImage__image}
                />
              )}
            </BpkBreakpoint>
            {(image.description || image.credit) && (
              <div className={STYLES.ContentBlockWithImage__creditRow}>
                {image.description && (
                  <BpkText
                    tagName="span"
                    textStyle={TEXT_STYLES.caption}
                    className={STYLES.ContentBlockWithImage__description}
                  >
                    {image.description}
                  </BpkText>
                )}
                {image.credit && (
                  <BpkText
                    tagName="span"
                    textStyle={TEXT_STYLES.caption}
                    className={STYLES.ContentBlockWithImage__credit}
                  >
                    {image.credit}
                  </BpkText>
                )}
              </div>
            )}
          </div>
        )}
        <div className={STYLES.ContentBlockWithImage__content}>
          <ContentBlock sections={sections} />
        </div>
      </div>
    </div>
  );
};

const ContentBlockWithImageWithErrorBoundary = withErrorBoundary(
  ContentBlockWithImage,
  'content-block-with-image-component',
);

export default ContentBlockWithImageWithErrorBoundary;
